import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { debounce } from 'lodash-es';
import { useEffect, useState } from 'react';
import { Component } from './Component.tsx';
import { buildDependencyKey } from './helpers/buildDependencyKey.ts';
import { type CompanyOption, type InnerQueryProps, type SearchCompanyResponse } from './types.ts';

const GET_SEARCH_COMPANY_RESULTS = "query GetSearchCompanyResults(\n  $first: Int = 5\n  $query: String!\n  $searchType: SearchType = COMPANY\n) {\n  search(\n    first: $first,\n    query: $query,\n    searchType: $searchType,\n  ) {\n    nodes {\n      ...on Company {\n        logoPath\n        name\n      }\n    }\n  }\n}\n";

export const InnerQuery = ({ ariaLabelledBy, name, setFieldValue, value }: InnerQueryProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState<CompanyOption[]>([]);

  const { execute, result } = useQuery<SearchCompanyResponse>(GET_SEARCH_COMPANY_RESULTS, {
    loading: true,
  });

  useEffect(() => {
    if (inputValue) {
      void execute({ variables: { query: inputValue } }, { data: { initiator: 'CompanyMultiSelectDropdown' } });
    }
  }, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const nodesKey = buildDependencyKey(result instanceof QueryError ? undefined : result?.data.search.nodes);

  useEffect(() => {
    if (!(result instanceof QueryError) && result?.data.search.nodes) {
      const companyOptions = result.data.search.nodes.map(company => ({
        id: company.id,
        logoPath: company.logoPath,
        name: company.name,
      }));

      setOptions(companyOptions);
    }
    // nodesKey is derived from result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodesKey]);

  const debouncedSetInputValue = debounce(setInputValue, 250, { maxWait: 1000 });

  return (
    <Component
      ariaLabelledBy={ariaLabelledBy}
      name={name}
      options={options}
      setFieldValue={setFieldValue}
      setInputValue={debouncedSetInputValue}
      value={value}
    />
  );
};
